<template>
    <div class="period-picker">
        <live-period-line
            v-if="!props.hideTimer"
            v-show="showTimer && isLivePeriod"
            ref="livePeriodLine"
            :use-trading-period="useTradingPeriod"
            class="live-period-line"
        />
        <div class="period-picker-and-timer">
            <div class="period-selection-buttons">
                <prime-button class="button-muted" @click="togglePeriodPickerOverlay($event, 'date')" :disabled="props.disabled">
                    {{ dayjs(dateObject).format('DD MMM YYYY') }}
                </prime-button>

                <prime-button class="button-muted" @click="updatePeriodByDirection('previous')" :disabled="props.disabled">
                    <font-awesome-icon :icon="['fas', 'chevron-left']" />
                </prime-button>
                <prime-button class="button-muted" @click="togglePeriodPickerOverlay($event, 'period')" :disabled="props.disabled">
                    {{ selectedPeriod }}
                </prime-button>
                <prime-button class="button-muted" @click="updatePeriodByDirection('next')" :disabled="props.disabled">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </prime-button>
                <prime-button
                    v-if="!isLivePeriod"
                    class="button-muted not-live"
                    @click="jumpToLiveDatePeriod"
                    v-tooltip="'Jump to live period'">
                    <font-awesome-icon
                        :icon="['fas', 'clock']"
                        :disabled="props.disabled"
                    />
                </prime-button>
                <font-awesome-icon
                    v-if="lastPeriodWithData"
                    v-tooltip="`Showing available BOAs from period ${lastPeriodWithData}`"
                    :icon="['fas', 'circle-exclamation']"
                />
            </div>
            <countdown-timer
                v-if="!props.hideTimer"
                v-show="showTimer && isLivePeriod"
                :use-trading-period="useTradingPeriod"
                ref="countdownTimer"
                @finished="moveToNextTradingWindow"
            />
        </div>
    </div>

    <popover ref="periodPickerOverlay" trigger="click">
        <div v-if="componentInView === 'period'" class="period-picker-wrapper">
            <title-bar class="space-between">
                Select period
            </title-bar>
            <div class="period-list">
                <prime-button
                    class="button-muted"
                    :class="{ 'selected-period': period === selectedPeriod, 'current-live-period': period === dataStore.periodData?.current.period }"
                    v-for="period in totalPeriods"
                    @click="selectPeriod(period)"
                    :key="`period-button-${period}`">{{
                        period }}</prime-button>
            </div>
        </div>
        <div v-else class="period-picker-wrapper">
            <date-picker :model-value="dateObject" inline @date-select="setDate" />
        </div>
    </popover>
</template>

<script lang="ts" setup>
import PrimeButton from 'primevue/button';
import Popover from 'primevue/popover';
import {
    ref, computed, watch, nextTick,
} from 'vue';
import TitleBar from '@/components/layout/TitleBar.vue';
import DatePicker from 'primevue/datepicker';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import CountdownTimer from '@/components/common/CountdownTimer.vue';
import LivePeriodLine from '@/components/powerMarkets/balancing/LivePeriodLine.vue';
import dayjs from 'dayjs';
import { getDatePeriods } from '@/utils/periodUtils.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useDataStoreActions } from '@/actions/powerMarkets/dataStoreActions.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import { getPeriodPageOverride, getPeriodToDisplayComputed } from '@/composables/usePeriodToDisplay.ts';

const dataStore = useDataStore();
const userSettingsStore = useUserSettingsStore();
const dataStoreActions = useDataStoreActions();

const props = defineProps<{
    modelValue: IDatePeriod;
    lastPeriodWithData?: number;
    periodOverrideMode?: PeriodTypeOverridesWithInherited;
    disabled?: boolean;
    hideTimer?: boolean;
}>();

const emit = defineEmits<{
    'update:model-value': [datePeriod: IDatePeriod];
    'update:period': [];
}>();

type ComponentView = 'period' | 'date';

const countdownTimer = ref<typeof CountdownTimer | null>(null);
const livePeriodLine = ref<InstanceType<typeof LivePeriodLine> | null>(null);
const componentInView = ref<ComponentView>('period');
const periodPickerOverlay = ref<InstanceType <typeof Popover> | null>(null);
const selectedPeriod = ref<number>(1);
const dateObject = ref<Date>(new Date());

const periodOverrideModeLocal = computed<PeriodTypeOverridesWithInherited>(() => props.periodOverrideMode || 'inherited');
const inheritedPeriodMode = getPeriodPageOverride();
const useTradingPeriod = computed(() => (periodOverrideModeLocal.value === 'inherited' ? inheritedPeriodMode.value : periodOverrideModeLocal.value) === 'trading');
const currentPeriod = getPeriodToDisplayComputed(periodOverrideModeLocal);

const isLivePeriod = computed<boolean>(() => selectedPeriod.value === currentPeriod.value.period && dayjs(dateObject.value).isSame(dayjs(), 'days'));

const totalPeriods = computed(() => getDatePeriods(dateObject.value));

const showTimer = computed<boolean>(() => userSettingsStore.userSettings.otherSettings.data.showTimer);
const autoRollOver = computed<boolean>(() => userSettingsStore.userSettings.otherSettings.data.autoRollover);

async function moveToNextTradingWindow (): Promise<void> {
    await dataStoreActions.getPeriodData();
}

function emitDateAndPeriod (): void {
    const liveDate = dayjs(dataStore.periodData?.current.date);
    const dateHasChanged = dateObject.value.setHours(0, 0, 0, 0) !== new Date().setHours(0, 0, 0, 0) || !liveDate.isSame(dayjs(), 'day');
    emit('update:model-value', { date: dateObject.value, period: selectedPeriod.value, changingDateAndPeriod: dateHasChanged });
}

function updatePeriodByDirection (direction: 'previous' | 'next'): void {
    if (direction === 'previous') {
        if (selectedPeriod.value === 1) {
            dateObject.value = dayjs(dateObject.value).subtract(1, 'day').toDate();
            selectedPeriod.value = totalPeriods.value;
        } else {
            selectedPeriod.value -= 1;
        }
    }
    if (direction === 'next') {
        if (selectedPeriod.value === totalPeriods.value) {
            selectedPeriod.value = 1;
            dateObject.value = dayjs(dateObject.value).add(1, 'day').toDate();
        } else {
            selectedPeriod.value += 1;
        }
    }
    emitDateAndPeriod();
}

function togglePeriodPickerOverlay (event: Event, component: ComponentView): void {
    componentInView.value = component;
    periodPickerOverlay.value?.toggle(event);
}

function selectPeriod (period: number): void {
    selectedPeriod.value = period;
    periodPickerOverlay.value?.hide();
    emitDateAndPeriod();
}

function setDate (date: Date): void {
    dateObject.value = date;
    periodPickerOverlay.value?.hide();
    emitDateAndPeriod();
}

function jumpToLiveDatePeriod (): void {
    dateObject.value = new Date();
    selectedPeriod.value = currentPeriod.value.period;

    emitDateAndPeriod();
}

function resetToNewCurrent (): void {
    jumpToLiveDatePeriod();
    if (countdownTimer.value) {
        countdownTimer.value.restart();
    }
    livePeriodLine.value?.restart();
}

function initialise (): void {
    selectedPeriod.value = props.modelValue.period || currentPeriod.value.period || 1;
    dateObject.value = props.modelValue.date;
}

watch(() => props.modelValue, (newDateAndPeriod) => {
    dateObject.value = newDateAndPeriod.date;
    selectedPeriod.value = newDateAndPeriod.period;
});

watch(() => dataStore.periodData, () => {
    if (autoRollOver.value) {
        resetToNewCurrent();
    }
});

watch(() => props.periodOverrideMode, () => {
    nextTick(() => {
        resetToNewCurrent();
    });
});

initialise();

</script>

<style lang="scss" scoped>
.period-picker {
    display: flex;
    flex-direction: column;
    gap: var(--extraSmallSpacing);
}

.period-picker-and-timer {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
}

.live-period-line {
    width: 100%;
    max-width: 30ch;
}

.period-selection-buttons {
    display: flex;
    position: relative;
    align-items: center;
    gap: var(--smallSpacing);
}

.period-list {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: var(--defaultSpacing);
    padding: var(--defaultSpacing);
    background: var(--neutral100);
}

.button-muted {
    background: var(--neutral0);
    color: var(--neutral900);
    border: 1px solid var(--neutral200);
    transition: all .1s ease-in-out;

    &:hover {
        background: var(--actionColour);
        color: var(--neutral0) !important;
    }

    &.not-live {
        border: 1px solid var(--accentColour);
        border-style: dotted;
        color: var(--accentColour);
    }
}

.selected-period {
    background: var(--accentColour);
    color: var(--neutral0);
}

.current-live-period {
    border: 1px solid var(--accentColour);
    border-style: dotted;

}

.lock{
    color: var(--warningYellow) !important;
}
</style>
